import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  Table,
  Button,
  Input,
  Col,
  Offcanvas,
  Spinner,
} from 'reactstrap';
import { useMutation } from '@tanstack/react-query';
import Icon from '@mdi/react';
import {
  mdiFileDownloadOutline,
  mdiClockPlusOutline,
  mdiNoteEditOutline,
  mdiAccountOutline,
} from '@mdi/js';
import { toast } from 'react-toastify';
import {
  FilterDropdown,
  ErrorModalWrapper,
  ErrorTable,
  TableFilterBubbles,
  TableSortDisplayArrow,
  UserAsyncAutocompleteSelect,
  DatePicker,
} from '../../components/Common';
import ManualPunchIn from '../../components/Widgets/PunchInWidget/ManualPunchIn';
import {
  tableColumns,
  dataConfig,
  renderTimesheetStatusLegend,
} from './constants';
import { useTableHook } from '../../components/hooks';
import { fetchAttendanceDetails } from '../../store/apis/attendanceService';
import { fetchTableColumnValues } from '../../store/apis/commonService';
import { fetchEmployeesAutoComplete } from '../../store/apis/autocompleteService';
import { fetchManualEventList } from '../../store/apis/eventService';
import { ExportUtil, DateUtil, AuthUtil } from '../../utils';

import './TimeSheet.scss';

function TimeSheet() {
  document.title = 'Time Sheet Enquiry | Envision';
  const [punchInOffcanvas, setPunchInOffcanvas] = useState(false);
  const [selectRowData, setSelectRowData] = useState(null);
  const [setCanvasType, setSetCanvasType] = useState('create');
  const [manualEventData, setManualEventData] = useState(null);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorData, setErrorData] = useState(null);
  const [canShowAction, setCanShowAction] = useState(true);

  const isLoggedIn = AuthUtil.getAuthUser()?.employeeId;

  const now = new Date();
  const fromDate = new Date(now.getFullYear(), now.getMonth(), 1);
  const toDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  const initialDefaultExternalFilters = {
    fromDate: DateUtil.formatDateObjToHtmlDate(fromDate),
    toDate: DateUtil.formatDateObjToHtmlDate(toDate),
    type: 'DETAIL',
  };

  const {
    data,
    columnData,
    pageRequest,
    setPageRequest,
    externalFilters,
    applyFilter,
    columnName,
    setColumnName,
    resetFilters,
    handleSetExternalFilters,
    applyExternalFilters,
    tableDataMutation,
    handleManualSort,
    clearSingleFilterValue,
    checkColumnIfSorted,
    selectAllRows,
    handleRowCheck,
    activeRows,
    isLoadingData,
    isLoadingColumn,
    handleAutoSort,
  } = useTableHook({
    defaultPageRequest: {
      ...initialDefaultExternalFilters,
      pageNumber: 1,
      pageSize: 0,
      sortColumn: 'Dt',
      sortDirection: 'ASC',
      filters: [],
      employeeId: AuthUtil.getAuthUser()?.employeeId,
    },
    fetchFn: fetchAttendanceDetails,
    fetchColumnFn: fetchTableColumnValues,
    queryKeyPrefix: 'AttendanceEnquiry',
    columnKeyPrefix: 'AttendanceEnquiryColumn',
    tableName: 'AttendanceEnquiry',
    defaultExternalFilters: {
      ...initialDefaultExternalFilters,
      employeeId: {
        value: AuthUtil.getAuthUser()?.employeeId,
        label: AuthUtil.getAuthUser()?.userFullName,
      },
    },
  });

  // Fetch manual event list
  const fetchManualEventMutation = useMutation({
    mutationFn: fetchManualEventList,
  });

  const togglePunchIn = () => {
    setPunchInOffcanvas(!punchInOffcanvas);
  };

  const handleFetchEvents = async entry => {
    const fmtNewDate = DateUtil.convertDdMmYyyyToHtmlDate(entry.Dt);
    if (!fmtNewDate) {
      toast.error('Invalid date format for fetching events.', {
        className: 'toast-error',
      });
      return;
    }
    const eventParams = {
      startDate: fmtNewDate,
    };

    try {
      const eventData = await fetchManualEventMutation.mutateAsync(eventParams);
      if (eventData && eventData.length > 0) {
        setManualEventData(eventData);
        setSelectRowData(entry);
        setSetCanvasType('update');
        togglePunchIn();
      } else if (eventData && eventData.length === 0) {
        toast.warning('There are no events scheduled on this date.', {
          className: 'toast-warning',
        });
      }
    } catch (error) {
      toast.error('Error fetching event data.', {
        className: 'toast-error',
      });
    }
  };

  const externalFiltersValidation = () => {
    if (!externalFilters.fromDate) {
      toast.error('Please select From Date', {
        className: 'toast-error',
      });
      return false;
    }
    if (!externalFilters.toDate) {
      toast.error('Please select To Date', {
        className: 'toast-error',
      });
      return false;
    }
    if (new Date(externalFilters.fromDate) > new Date(externalFilters.toDate)) {
      toast.error('To Date should be greater than equal to From Date.', {
        className: 'toast-error',
      });
      return false;
    }
    return true;
  };

  // Handle the error modal
  const handleErrorModal = errorResponseData => {
    setIsErrorModalOpen(true);
    setErrorData(errorResponseData);
  };

  const handleExportExcel = async () => {
    const exportData = data?.data;
    if (!exportData || exportData.length === 0) {
      toast.error('No data found to export', {
        className: 'toast-error',
      });
      return;
    }
    const timesTamp = new Date().toISOString().slice(0, 10);
    const exportResponseData = await tableDataMutation.mutateAsync({
      ...pageRequest,
      pageNumber: 1,
      pageSize: 0,
    });

    let selectedData = exportResponseData?.data;

    if (activeRows?.length > 0) {
      selectedData = activeRows.map(index => exportResponseData?.data[index]);
      if (selectedData.length === 0) {
        toast.error('No selected data found to export', {
          className: 'toast-error',
        });
        return;
      }
    }

    ExportUtil.downloadExcel(selectedData, {
      fileName: `TimeSheet_Enquiry_${timesTamp}.xlsx`,
      columns: {
        Dt: 'Date',
        DayName: 'Day',
        TimeIn: 'Time In',
        TimeOut: 'Time Out',
        Hrs: 'Hours',
        M_TimeIn: 'Manual Time In',
        M_TimeOut: 'Manual Time Out',
        M_Hrs: 'Manual Hours',
        Status: 'Status',
      },
    });
  };

  const renderTableBody = () => {
    const tableData = data?.data;

    if (!tableData || tableData.length === 0) {
      return (
        <tr>
          <td className="text-center" colSpan={11}>
            {isLoadingData ? (
              <>
                <Spinner color="dark" size="sm" />
                <span className="ms-2">Loading...</span>
              </>
            ) : (
              'No data found!'
            )}
          </td>
        </tr>
      );
    }

    const rowBackgroundColor = {
      Holiday: '#EEEEEE',
      Today: '#C6E4CB',
    };

    return tableData?.map((entry, index) => (
      <tr
        key={entry.id}
        style={{
          backgroundColor: rowBackgroundColor[entry.Status] || '',
        }}
      >
        <td className="py-2">
          <div className="form-check">
            <Input
              className="form-check-input blue-checkbox"
              type="checkbox"
              name="chk_child"
              checked={activeRows?.includes(index)}
              onChange={e => handleRowCheck(index, e.target.checked)}
            />
          </div>
        </td>
        <td className="px-2 py-2">
          {(() => {
            const [day, month, year] = entry.Dt.split('/');
            const parsedDate = new Date(`${year}-${month}-${day}`);
            return parsedDate.toLocaleDateString('en-GB');
          })()}
        </td>
        <td className="px-2 py-2">{entry.DayName || '-'}</td>
        <td className="px-2 py-2">{entry.TimeIn || '-'}</td>
        <td className="px-2 py-2">{entry.TimeOut || '-'}</td>
        <td className="px-2 py-2">{entry.Hrs || '-'}</td>
        <td className="px-2 py-2">{entry.M_TimeIn || '-'}</td>
        <td className="px-2 py-2">{entry.M_TimeOut || '-'}</td>
        <td className="px-2 py-2">{entry.M_Hrs}</td>
        <td className="px-2 py-2 text-center">
          {renderTimesheetStatusLegend(entry.Status)}
        </td>
        {canShowAction && (
          <td className="px-2 py-2 text-center">
            <div className="d-flex align-items-center justify-content-center gap-2">
              <Button
                size="sm"
                className="non-btn"
                color="link"
                onClick={() => {
                  handleFetchEvents(entry);
                }}
              >
                <Icon
                  path={mdiNoteEditOutline}
                  size={0.8}
                  color="#017EBD"
                  title="Please click to edit the manual timesheet entry"
                />
              </Button>
              <Button
                size="sm"
                className="non-btn"
                color="link"
                onClick={() => {
                  setSelectRowData(entry);
                  setSetCanvasType('create');
                  togglePunchIn();
                }}
              >
                <Icon
                  path={mdiClockPlusOutline}
                  size={0.8}
                  color="#079455"
                  title="Please click to add a manual timesheet entry"
                />
              </Button>
            </div>
          </td>
        )}
      </tr>
    ));
  };

  return (
    <div className="page-content timesheet-page">
      <Card className="timesheet-card">
        <CardHeader
          className="d-flex justify-content-between align-items-center pt-2 pb-2"
          style={{ borderRadius: '10px 10px 0 0' }}
        >
          <div>
            <span className="fs-5" style={{ color: '#000', fontWeight: '600' }}>
              Time Sheet Enquiry
            </span>
          </div>
          <div className="d-flex gap-2">
            <Button className="custom-light-btn" onClick={handleExportExcel}>
              <span>Download as Excel</span>
              <Icon path={mdiFileDownloadOutline} size={0.8} />
            </Button>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          <div className="external-filter gap-2 mx-2 mt-2 p-3 d-flex flex-row">
            <div
              className="external-filter-col me-1"
              style={{ minWidth: '25%' }}
            >
              <b>Employee</b>
              <UserAsyncAutocompleteSelect
                autoCompleteFn={input =>
                  fetchEmployeesAutoComplete({
                    type: 'EMPLOYEE',
                    prefixText: input,
                  })
                }
                mappedFn={rawData =>
                  rawData.map(item => ({
                    value: item.Cd,
                    label: item.Name,
                    name: item.Name,
                    code: item.Cd,
                    designation: item.Designation,
                    image: item.Imagefile,
                  }))
                }
                onChange={e => {
                  handleSetExternalFilters('employeeId', e);
                }}
                placeholder="Search by Employee Name & ID"
                isClearable={false}
                isSearchable
                value={externalFilters?.employeeId}
                setSearchClicked={false}
                inputIcon={
                  <Icon path={mdiAccountOutline} size={0.8} color="#667085" />
                }
                rightKeyAssessor="code"
                searchEmployee={
                  AuthUtil.getAuthUser()?.employeeId?.toLowerCase() !==
                  AuthUtil.getAuthUser()?.userName?.toLowerCase()
                }
              />
            </div>
            <Col className="external-filter-col me-2">
              <b>From Date</b>
              <DatePicker
                onChange={e => handleSetExternalFilters('fromDate', e)}
                value={externalFilters.fromDate}
                className="form-control external-filter-input"
              />
            </Col>
            <Col className="external-filter-col me-2">
              <b>To Date</b>
              <DatePicker
                onChange={e => handleSetExternalFilters('toDate', e)}
                value={externalFilters.toDate}
                className="form-control external-filter-input"
              />
            </Col>
            <Col className="d-flex align-items-center justify-content-end">
              <div className="external-filter-btns">
                <button
                  type="button"
                  className="custom-btn fw-semibold"
                  onClick={() => {
                    if (!externalFiltersValidation()) {
                      return;
                    }
                    const selectedEmpId = externalFilters?.employeeId?.value;
                    const loggedInEmpId = isLoggedIn;

                    // Only after clicking search, decide whether to show action
                    setCanShowAction(selectedEmpId === loggedInEmpId);
                    applyExternalFilters({
                      ...externalFilters,
                      employeeId: externalFilters?.employeeId?.value,
                    });
                  }}
                >
                  Search
                </button>
              </div>
            </Col>
          </div>
          <div className="my-2 px-2">
            <TableFilterBubbles
              allFilters={pageRequest?.filters || []}
              clearSingleFilterValue={clearSingleFilterValue}
              labelMapping={tableColumns}
              clearAllFilters={resetFilters}
            />
          </div>
          <div className="table-responsive timesheet-table-container">
            <Table hover className="custom-table align-middle text-nowrap">
              <thead>
                <tr>
                  <th>
                    <div className="form-check px-0">
                      <Input
                        className="form-check-input blue-checkbox"
                        type="checkbox"
                        id="checkbox"
                        checked={
                          activeRows?.length > 0 &&
                          activeRows?.length === data?.data?.length
                        }
                        onChange={e => selectAllRows(e?.target?.checked)}
                      />
                    </div>
                  </th>
                  {Object.entries(tableColumns).map(([key, value]) => (
                    <th
                      key={key}
                      className={`px-2 ${key === 'Status' ? 'text-center' : ''}`}
                    >
                      <div
                        className={`d-flex flex-row align-items-center gap-2 ${key === 'Status' ? 'justify-content-center' : 'justify-content-start'}`}
                      >
                        <div
                          className="p-0 cursor-pointer"
                          onClick={() => {
                            handleAutoSort(key);
                          }}
                        >
                          {value}
                        </div>
                        <TableSortDisplayArrow
                          direction={checkColumnIfSorted(key)}
                        />
                        <FilterDropdown
                          handleSetColumnName={() => {
                            setColumnName(key);
                          }}
                          setColumnName={setColumnName}
                          columnData={columnData}
                          applyFilter={applyFilter}
                          columnName={columnName}
                          pageRequest={pageRequest}
                          setPageRequest={setPageRequest}
                          handleManualSort={handleManualSort}
                          isLoading={isLoadingColumn}
                        />
                      </div>
                    </th>
                  ))}
                  {canShowAction && <th className="text-center">Action</th>}
                </tr>
              </thead>
              <tbody>{renderTableBody()}</tbody>
            </Table>
          </div>
        </CardBody>
      </Card>
      <Offcanvas
        isOpen={punchInOffcanvas}
        toggle={() => setPunchInOffcanvas(!punchInOffcanvas)}
        direction="end"
        style={{ width: '40vw' }}
      >
        <ManualPunchIn
          toggle={togglePunchIn}
          setPunchInOffcanvas={setPunchInOffcanvas}
          data={selectRowData}
          type={setCanvasType}
          eventData={manualEventData}
          handleErrorModal={handleErrorModal}
        />
      </Offcanvas>
      <ErrorModalWrapper
        isModalOpen={isErrorModalOpen}
        toggle={() => setIsErrorModalOpen(!isErrorModalOpen)}
        errorMessage={errorData?.message}
        onButtonClick={() => setIsErrorModalOpen(!isErrorModalOpen)}
        size="xl"
      >
        <ErrorTable errorData={errorData?.errorData} dataConfig={dataConfig} />
      </ErrorModalWrapper>
    </div>
  );
}

export default TimeSheet;
